import React from 'react';
import {
  IStyledSearchBoxProps,
  IStyledSearchList,
  IStyledSearchOption
} from './SearchBox.style';
/**
 * Value returned when making a selection
 */
export interface ISearchboxOption<T = any> {
  /**
   * Label describing the searchBoxOption
   */
  label: string;
  /**
   * Unique value representing the searchBoxOption
   */
  value: string | number;
  /**
   * Any additional value that could describe a searchBoxOption
   */
  metaData?: T;
}

/**
 * Keyboard navigation type
 */
export enum NavType {
  UP,
  DOWN,
  TOP,
  BOTTOM
}

/**
 * Scroll options
 *
 * To enable scrolling, set either searchListRows or searchListHeight.
 * Recommend to use searchListRows with ellipsis prop on SearchOption
 */
export interface IScrollOption {
  optionHeight: number;
  /**
   * The maximum number of rows to show when scrolling
   */
  searchListRows?: number;
  /**
   * The maximum height of the search list when scrolling
   */
  searchListHeight?: number;
  /**
   * The index number assigned to each option, this should be the index that
   * represents the order of the search option in the list
   */
  index?: number;
}

/**
 * Additional SearchList props
 */
export interface ISearchList extends IScrollOption {
  type: NavType;
  nextIndex: number;
}

export interface IScrollEventOption {
  scrollTop: number;
}

/**
 * Use reducer actions
 */
export type Action =
  | { type: ActionTypes.CHANGE; payload: string }
  | { type: ActionTypes.SELECT_CLICK; payload: string }
  | { type: ActionTypes.SELECT_MULTI_CLICK; payload: ISearchboxOption[] }
  | { type: ActionTypes.KEYBOARD_NAV; payload: ISearchboxOption }
  | { type: ActionTypes.SELECT_KEYBOARD }
  | { type: ActionTypes.SELECT_MULTI_KEYBOARD; payload: ISearchboxOption[] }
  | { type: ActionTypes.INITIAL_KEYBOARD_NAV }
  | { type: ActionTypes.ESCAPE }
  | { type: ActionTypes.BLUR }
  | { type: ActionTypes.INTERACT }
  | { type: ActionTypes.BLUR_MULTI }
  | { type: ActionTypes.FOCUS_MULTI }
  | { type: ActionTypes.CONTINUE }
  | { type: ActionTypes.CLEAR }
  | { type: ActionTypes.LOAD; payload: ISearchboxOption }
  | { type: ActionTypes.SCROLL; payload: IScrollEventOption };
/**
 * Types of actions
 */
export enum ActionTypes {
  CHANGE = 'CHANGE',
  CLEAR = 'CLEAR',
  ESCAPE = 'ESCAPE',
  INTERACT = 'INTERACT',
  BLUR = 'BLUR',
  BLUR_MULTI = 'BLUR_MULTI',
  FOCUS_MULTI = 'FOCUS_MULTI',
  INITIAL_KEYBOARD_NAV = 'INITIAL_KEYBOARD_NAV',
  KEYBOARD_NAV = 'KEYBOARD_NAV',
  SELECT_CLICK = 'SELECT_CLICK',
  SELECT_KEYBOARD = 'SELECT_KEYBOARD',
  SELECT_MULTI_CLICK = 'SELECT_MULTI_CLICK',
  SELECT_MULTI_KEYBOARD = 'SELECT_MULTI_KEYBOARD',
  CONTINUE = 'CONTINUE',
  LOAD = 'LOAD',
  SCROLL = 'SCROLL'
}
/**
 * All interaction states
 */
export enum Interactions {
  IDLE = 'IDLE',
  ACTING = 'ACTING',
  NAVIGATING = 'NAVIGATING'
}
/**
 * Interaction states where suggested options are visible
 */
export enum VisibleStates {
  ACTING = 'ACTING',
  NAVIGATING = 'NAVIGATING'
}
/**
 * SearchBox Context
 */
export interface ISearchContext {
  /**
   * flag denoting whether the list is visible, based on interactionState
   */
  isVisible: boolean;
  /**
   * Internal state of SearchBox
   */
  state: ISearchBoxState;
  /**
   * Reference to SearchBoxInput
   */
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  /**
   * Reference to List of SearchBoxOptions
   */
  optionListRef: React.MutableRefObject<HTMLUListElement | null>;
  /**
   * List of labels available for selection
   */
  optionsRef: React.MutableRefObject<ISearchboxOption[] | null>;
  /**
   * Flag denoting whether searchbox has unmounted or not
   */
  isMounted: React.MutableRefObject<boolean>;
  /**
   * Flag denoting whether more than one value is selectable
   */
  multi?: boolean;
  /**
   * User controlled selected values
   */
  controlledValue?: ISearchboxOption | ISearchboxOption[];
  /**
   * Input is in an error state
   */
  hasError?: boolean;
  /**
   * Callback invoke when user makes a selection
   */
  onChange?: (
    /**
     * Either a list of currently selected values or a single selected value
     */
    currentlySelectedValue: ISearchboxOption[] | ISearchboxOption | null,
    /**
     * The currently selected value in multi select or undefined in single select
     */
    valueProp?: ISearchboxOption
  ) => void;
  /**
   * useReducer dispatch
   */
  dispatch: (a: Action) => void;
}
/**
 * SearchBox internal state
 */
export interface ISearchBoxState {
  /**
   * Current state of interaction 'IDLE' | 'ACTING' | 'NAVIGATING'
   */
  interactionState: Interactions;
  /**
   * Current value after keyboard navigation
   */
  currentKeyboardValue: ISearchboxOption | null;
  /**
   * Current input value, always controlled by internal state
   */
  currentInputValue: string;
  /**
   * Current ScrollTop
   */
  currentScrollTop: number;
  /**
   * Scroll interval. Each search option may have different scrollInterval.
   * The value is set when each option is mounted.
   */
  optionsHeight: number[];
  /**
   * The maximum height of the search list when scrolling
   */
  searchListHeight?: number;
  /**
   * The maximum number of rows to show when scrolling
   */
  searchListRows?: number;
  /**
   * List of currently selected values in multiselect mode
   */
  selectedMultiValues: ISearchboxOption[];
  /**
   * A reference to the previous use action dispatched
   */
  previousAction?: ActionTypes;
}
/**
 * SearcBox Props
 */
export interface ISearchBoxProps extends IStyledSearchBoxProps {
  /**
   * Unique identifier for this input item. Required for screen readers
   * to associate this component's `id` with the `id` of the
   * input component.
   */
  inputId: string;

  /**
   * Callback invoked when user is searching, the current search value is passed
   */
  onSearch?: (s: string) => void;
  /**
   * Callback invoked when selected input value changes,
   */
  onChange?: (
    /**
     * Either a list of currently selected values or a single selected value
     */
    currentlySelectedValue: ISearchboxOption[] | ISearchboxOption | null,
    /**
     * The currently selected value in multi select or undefined in single select
     */
    valueProp?: ISearchboxOption
  ) => void;
  /**
   * Callback invoked when user clicks the search input
   */
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  /**
   * Callback invoked when input is blured
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback invoked when input is focused
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Currently selected option
   */
  value?: ISearchboxOption | ISearchboxOption[];
  /**
   * Allows multiple selections to be made
   */
  multi?: boolean;
  /**
   * Placeholder for text input
   */
  placeholder?: string;
  /**
   * Will automatically select the current input value,
   * allowing the user to immediately backspace and delete the entire value
   */
  userSelectOnClick?: boolean;
  /**
   * Will treat SearchBox like a select when true.
   */
  openOnClick?: boolean;
  /**
   * Icon to show on the right of the input
   */
  icon?: React.ReactElement | string;
  /**
   * disables the input
   */
  disabled?: boolean;
  /**
   * marks input as required
   */
  required?: boolean;
  /**
   * input error state
   */
  hasError?: boolean;
  /**
   *  Override the default list separator character, which is a comma ','
   */
  delimiter?: string;

  children?: React.ReactNode;
}
export interface ISearchInputProps {
  inputId: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  icon?: React.ReactElement | string;
  userSelectOnClick?: boolean;
  openOnClick?: boolean;
  delimiter: string;
  isOpen?: boolean;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onSearch?: (s: string) => void;
}

/**
 * Data supplied in an onScroll event on SearchList
 */
export interface IScrollEvent {
  /**
   * Current ScrollTop for SearchList
   */
  scrollTop?: number;
  /**
   * The computed SearchList visible client height
   */
  visibleHeight?: number;

  /**
   * The computed SearchList total client height
   */
  totalHeight?: number;
}

export interface ISearchListProps extends IStyledSearchList {
  /**
   * If footerContent is present, the footer will show in the SearchList.
   */
  footerContent?: React.ReactElement | string;
  /**
   * The maximum height of the search list when scrolling
   */
  searchListHeight?: number;
  /**
   * The maximum number of rows to show when scrolling
   */
  searchListRows?: number;
  /**
   * This function is called when SearchList is scrolled.
   */
  onScroll?: (e: IScrollEvent) => void;

  children?: any;
}
/**
 * Selectable option
 */
export interface ISearchOptionProps<T = any> extends IStyledSearchOption {
  /**
   * Label describing the searchBoxOption
   */
  label: string;
  /**
   * Unique value representing the searchBoxOption
   */
  value: string | number;
  /**
   * Any additional value that could describe a searchBoxOption
   */
  metaData?: T;
  /**
   * user provided onClick callback
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}
export interface IMultiselectOption {
  label: string;
  value: string | number;
}

/**
 * SelectAll option
 */
export interface SelectAllProps extends IStyledSearchOption {
  /**
   * Label describing "Select All" option. Default to "Select All".
   */
  label?: string;
}
